import React, { useCallback, useEffect, useMemo } from 'react';

import { Tabs } from '@motorway/motorway-storybook-cra';

import cypressIds from 'Components/cypress_ids.json';
import { triggerBuyingFormatSelectedEvent } from 'Services/analytics/events';
import { useAiCompanionExperimentation } from 'Services/optimizely/experimentations/useAiCompanionExperimentation';
import { useSetDefaultConfig } from 'Stores/FilterStore/VehicleListFilter/VehicleListFilterStore';
import { useListType, useSetListType } from 'Stores/ListStore/ListStore';
import type { ListType } from 'Types/listType';
import { LIST_TYPES } from 'Utilities/consts';
import { applyCypressData } from 'Utilities/helpers';
import useFeatureToggle, { FEATURES } from 'Utilities/hooks/useFeatureToggle';
import useSortOrder from 'Utilities/hooks/vehicleList/useSortOrder/useSortOrder';

import { VEHICLE_LIST_FILTER_NAME } from '../VehicleListFilters.consts';

import { VehicleListTabContent } from './VehicleListTabContent';
import { VEHICLE_LIST_TAB_OPTIONS } from './VehicleListTabs.const';

import styles from './VehicleListTabs.module.scss';

const VehicleListTabs: React.FC = () => {
	const listType = useListType();
	const setDefaultConfig = useSetDefaultConfig();
	const setListType = useSetListType();
	const { defaultSort } = useSortOrder();
	const enableAiCompanion = useAiCompanionExperimentation();

	const HIDDEN_TABS = {
		[LIST_TYPES.aiCompanion]: !enableAiCompanion,
		[LIST_TYPES.buyItNow]: !!useFeatureToggle(FEATURES.removeBuyItNow),
	} as Record<string, boolean>;

	const vehicleListTabOptions = VEHICLE_LIST_TAB_OPTIONS.filter((option) => !HIDDEN_TABS[option]);

	useEffect(() => {
		setDefaultConfig({
			id: VEHICLE_LIST_FILTER_NAME.SORT,
			value: defaultSort,
		});
	}, [defaultSort, setDefaultConfig]);

	const handleTabChange = useCallback(
		(option: ListType) => {
			triggerBuyingFormatSelectedEvent(option);
			setListType(option);
		},
		[setListType],
	);

	const items = useMemo(
		() =>
			vehicleListTabOptions.map((id) => ({
				content: <VehicleListTabContent id={id} />,
				id,
				onChange: handleTabChange,
			})),
		[handleTabChange, vehicleListTabOptions],
	);

	return (
		<div className={styles.container}>
			<Tabs
				className={styles.tabs}
				items={items}
				selectedId={listType}
				tabClassName={styles.tab}
				{...applyCypressData(cypressIds.buttons.vehicleListTabs.container[listType])}
			/>
		</div>
	);
};

export default VehicleListTabs;
